import React, { useState, useEffect } from 'react';
import { Save, User, MapPin, CreditCard, Lock, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const AccountSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  const [addressData, setAddressData] = useState({
    street: '',
    zip: '',
    city: '',
    country: 'Deutschland'
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || ''
        });
        setAddressData(data.address || {
          street: '',
          zip: '',
          city: '',
          country: 'Deutschland'
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...profileData,
          address: addressData
        })
      });

      if (response.ok) {
        toast.success('Profil erfolgreich aktualisiert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        toast.success('Passwort erfolgreich geändert!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Fehler beim Ändern des Passworts');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Lade Profil...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Persönliche Informationen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Vorname</Label>
              <Input
                value={profileData.first_name}
                onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                placeholder="Max"
              />
            </div>
            <div>
              <Label>Nachname</Label>
              <Input
                value={profileData.last_name}
                onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                placeholder="Mustermann"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>E-Mail-Adresse</Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                placeholder="max@beispiel.de"
              />
            </div>
            <div>
              <Label>Telefonnummer</Label>
              <Input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                placeholder="+49 123 456789"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Adresse
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Straße & Hausnummer</Label>
            <Input
              value={addressData.street}
              onChange={(e) => setAddressData({...addressData, street: e.target.value})}
              placeholder="Musterstraße 123"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>PLZ</Label>
              <Input
                value={addressData.zip}
                onChange={(e) => setAddressData({...addressData, zip: e.target.value})}
                placeholder="12345"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Stadt</Label>
              <Input
                value={addressData.city}
                onChange={(e) => setAddressData({...addressData, city: e.target.value})}
                placeholder="Berlin"
              />
            </div>
          </div>

          <div>
            <Label>Land</Label>
            <Input
              value={addressData.country}
              onChange={(e) => setAddressData({...addressData, country: e.target.value})}
              placeholder="Deutschland"
            />
          </div>

          <Button onClick={saveProfile} disabled={saving} className="rounded-none text-white" >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Änderungen speichern'}
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Passwort ändern
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Aktuelles Passwort</Label>
            <Input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
              placeholder="••••••••"
            />
          </div>

          <div>
            <Label>Neues Passwort</Label>
            <Input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
              placeholder="••••••••"
            />
          </div>

          <div>
            <Label>Neues Passwort bestätigen</Label>
            <Input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
              placeholder="••••••••"
            />
          </div>

          <Button onClick={changePassword} variant="outline" className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]">
            <Lock className="h-4 w-4 mr-2" />
            Passwort ändern
          </Button>
        </CardContent>
      </Card>

      {/* Delete Account */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Konto löschen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Wenn Sie Ihr Konto löschen, werden alle Ihre Daten unwiderruflich entfernt.
          </p>
          <Button variant="destructive" disabled>
            Konto löschen (Bald verfügbar)
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountSettings;
